import { tw } from '@/lib/utils'

import type { SubscribeThemeTokens } from './theme'
import type { SubscribeFormContent } from './types'

// A small "signal" mark — four equalizer bars — as a quiet, contained nod to
// sound/hearing. Inherits the eyebrow color via `bg-current`. Animated via the
// global `soundBar` keyframes; the static heights show under reduced motion.
const BAR_HEIGHTS = ['40%', '85%', '55%', '100%']
const EqualizerMark = () => (
  <span aria-hidden className="inline-flex h-3 items-end gap-[2px]">
    {BAR_HEIGHTS.map((height, i) => (
      <span
        key={i}
        className="w-[2px] rounded-full bg-current [animation:soundBar_1.6s_ease-in-out_infinite] motion-reduce:animate-none"
        style={{ height, animationDuration: `${1.3 + i * 0.25}s`, animationDelay: `${i * 0.18}s` }}
      />
    ))}
  </span>
)

const CheckIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden className={tw('mt-0.5 h-[18px] w-[18px] shrink-0', className)}>
    <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.4" />
    <path
      d="M6 10.2l2.6 2.6L14 7.5"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

interface EditorialColumnProps {
  content: SubscribeFormContent
  tokens: SubscribeThemeTokens
}

export const EditorialColumn = ({ content, tokens }: EditorialColumnProps) => {
  const stacked = content.align === 'stacked'

  return (
    <div className={tw('flex flex-col', stacked && 'items-center text-center')}>
      {content.eyebrow && (
        <span
          className={tw(
            'inline-flex items-center gap-2.5 font-mono text-[11px] font-medium tracking-[0.28em] uppercase',
            stacked && 'justify-center',
            tokens.eyebrow
          )}
        >
          <EqualizerMark />
          {content.eyebrow}
        </span>
      )}
      <h2
        className={tw('mt-3 text-[1.95rem] leading-[1.08] tracking-[-0.02em] sm:text-[2.3rem]', tokens.heading)}
        style={{ fontFamily: 'var(--font-fraunces), Georgia, serif', fontWeight: 500 }}
      >
        {content.heading}
      </h2>
      {content.subheading && (
        <p className={tw('mt-4 max-w-md text-[1rem] leading-[1.55] tracking-[-0.01em]', tokens.subheading)}>
          {content.subheading}
        </p>
      )}
      {content.benefits.length > 0 && (
        <ul className={tw('mt-6 flex flex-col gap-2.5', stacked && 'items-start text-left')}>
          {content.benefits.map((benefit) => (
            <li key={benefit} className={tw('flex items-start gap-2.5 text-[0.92rem] leading-[1.4]', tokens.benefit)}>
              <CheckIcon className={tokens.benefitIcon} />
              <span>{benefit}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
